/**
 * Saisie de la quantité (specs §6.3 et §6.4) — dernier écran avant validation.
 *
 * ⚠️ **« au choix en unité de base (g/ml) ou via une portion prédéfinie, sans
 * quitter l'écran »** (specs §6.3). Les portions sont donc des segments posés à
 * côté de l'unité de base, pas un menu ni un second écran : changer de mode est
 * un tap, et le champ de quantité reste sous le doigt.
 *
 * ⚠️ **Les macros d'un produit Open Food Facts sont affichées et éditables ici**
 * (specs §6.4 : « traitées comme peu fiables par défaut »). Elles ne le sont pas
 * pour un aliment de la base personnelle : ces valeurs-là sont déjà les
 * siennes, et l'endroit pour les changer est la fiche, pas le journal.
 *
 * ⚠️ **Valider un produit OFF l'importe d'abord dans la base personnelle**, avec
 * les valeurs telles qu'elles sont à l'écran. C'est le mécanisme du §6.4 (« copié
 * dans la base perso puis corrigé »), et il a une conséquence heureuse : la
 * deuxième fois qu'on ajoutera ce produit, il sera trouvé en base et **aucun
 * appel réseau n'aura lieu** (specs §7.2). Deux allers-retours à la première
 * utilisation, un seul ensuite.
 *
 * ⚠️ Le total affiché n'est qu'un **aperçu** (§5.1) : le serveur recalcule et
 * c'est sa réponse qui fait foi. Voir `lib/foodPreview.ts`.
 */

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import type { LogEntryInput, MacroSet } from '../../../../shared/types';
import { createFood } from '../../api/foods';
import { Button, Card } from '../../components/nourri';
import { MacroDot } from '../../components/ui/MacroMeter';
import { ErrorNote, Hint, NumberField } from '../../components/ui/primitives';
import { newClientId } from '../../lib/dates';
import {
  baseQuantityOf,
  composeFoodLabel,
  composePortionLabel,
  previewMacrosFor,
} from '../../lib/foodPreview';
import { fieldsOf, macrosOf, toNumber, type MacroFields } from '../../lib/formFields';
import { formatMacro, MACRO_ROWS } from '../../lib/macros';
import type { EntryPreview } from '../../hooks/useDay';
import type { FoodPick } from './addTypes';

/** Une option de quantité : l'unité de base, ou une portion nommée. */
interface QuantityMode {
  /** `null` = unité de base. */
  portionIndex: number | null;
  label: string;
  /** Valeur d'une unité de ce mode, en unité de base. */
  baseValue: number;
}

/** Quantité proposée d'emblée, en unité de base — la plus courante à la pesée. */
const DEFAULT_BASE_QUANTITY = '100';

/** Quantité proposée quand on bascule sur une portion. */
const DEFAULT_PORTION_QUANTITY = '1';

export function QuantityStep({
  pick,
  mealName,
  initialQuantity,
  initialPortionLabel,
  onBack,
  onValidate,
}: {
  pick: FoodPick;
  mealName: string;
  /** Correction d'une entrée existante : sa quantité, telle qu'elle a été figée. */
  initialQuantity?: number;
  /** Libellé de portion figé, pour retrouver le mode d'origine. */
  initialPortionLabel?: string | null;
  /** Absent en correction d'une entrée : il n'y a pas de liste où revenir. */
  onBack?: () => void;
  onValidate: (input: LogEntryInput, preview: EntryPreview) => void;
}) {
  const isOff = pick.kind === 'off';
  const name = isOff ? pick.product.name : pick.food.name;
  const brand = isOff ? pick.product.brand : pick.food.brand;
  const baseUnit = isOff ? pick.product.baseUnit : pick.food.baseUnit;

  /*
   * Les macros d'un produit OFF vivent dans l'état du composant, parce qu'elles
   * sont éditables (§6.4). Celles d'un aliment de la base n'y entrent jamais :
   * elles sont lues directement, ce qui rend impossible de les modifier par
   * mégarde depuis le journal.
   */
  const [offFields, setOffFields] = useState<MacroFields>(() =>
    isOff ? fieldsOf(pick.product.per100) : { protein: '', carbs: '', fat: '', kcal: '' },
  );
  const per100: MacroSet = isOff ? macrosOf(offFields) : pick.food.per100;

  const portions: QuantityMode[] = isOff
    ? pick.product.servingQuantity === null
      ? []
      : [
          {
            portionIndex: 0,
            label: pick.product.servingLabel ?? '1 portion',
            baseValue: pick.product.servingQuantity,
          },
        ]
    : pick.food.portions.map((portion, index) => ({
        portionIndex: index,
        label: portion.label,
        baseValue: portion.quantity,
      }));

  const modes: QuantityMode[] = [
    { portionIndex: null, label: baseUnit, baseValue: 1 },
    ...portions,
  ];

  /*
   * Mode d'origine d'une correction : on retrouve la portion par son **libellé
   * figé** (« 1 tranche (32 g) »), reconstruit à l'identique depuis les portions
   * actuelles de la fiche.
   *
   * ⚠️ Si la portion a été renommée ou supprimée depuis, le libellé ne
   * correspond plus et on retombe sur l'unité de base — visiblement, pas en
   * silence : la quantité affichée devient celle de l'entrée, exprimée dans une
   * autre unité, et le bandeau d'aperçu le montre aussitôt. Deviner à la place
   * de l'utilisateur serait pire.
   */
  const initialModeIndex = (() => {
    if (initialPortionLabel === undefined || initialPortionLabel === null) return 0;
    const found = modes.findIndex(
      (item) =>
        item.portionIndex !== null &&
        composePortionLabel(item.label, item.baseValue, baseUnit) === initialPortionLabel,
    );
    return found === -1 ? 0 : found;
  })();

  const [modeIndex, setModeIndex] = useState(initialModeIndex);
  const [quantity, setQuantity] = useState(
    initialQuantity === undefined ? DEFAULT_BASE_QUANTITY : String(initialQuantity),
  );

  /*
   * ⚠️ Expression totale, sans retour anticipé : `modes` contient toujours au
   * moins l'unité de base, mais `noUncheckedIndexedAccess` l'ignore. Un
   * `if (!mode) return null` placé ici serait un retour **avant** le `useMutation`
   * ci-dessous — donc un nombre de hooks variable d'un rendu à l'autre.
   */
  const mode: QuantityMode = modes[modeIndex] ??
    modes[0] ?? { portionIndex: null, label: baseUnit, baseValue: 1 };

  const typed = toNumber(quantity);
  const baseQuantity = baseQuantityOf(
    typed,
    mode.portionIndex === null ? undefined : mode.baseValue,
  );
  const preview = previewMacrosFor(per100, baseQuantity);
  const canSubmit = typed > 0;

  /**
   * Import du produit OFF — première moitié de la validation, uniquement pour
   * la voie `off`. Idempotent par `clientId` **et** par `offCode` côté serveur :
   * un double tap ne crée pas deux fiches.
   */
  const importFood = useMutation({
    mutationFn: () =>
      createFood({
        clientId: newClientId(),
        name,
        brand,
        barcode: isOff ? pick.product.code : null,
        offCode: isOff ? pick.product.code : null,
        baseUnit,
        per100,
        portions: portions.map((portion) => ({
          label: portion.label,
          quantity: portion.baseValue,
        })),
      }),
  });

  const submit = () => {
    if (!canSubmit) return;

    const buildPreview = (portionLabel: string | null): EntryPreview => ({
      label: composeFoodLabel(name, brand),
      quantity: mode.portionIndex === null ? baseQuantity : typed,
      unit: mode.portionIndex === null ? baseUnit : 'portion',
      portionLabel,
      macros: preview,
    });

    if (pick.kind === 'food') {
      const portion =
        mode.portionIndex === null ? undefined : pick.food.portions[mode.portionIndex];
      onValidate(
        {
          type: 'food',
          foodId: pick.food.id,
          quantity: mode.portionIndex === null ? baseQuantity : typed,
          ...(portion === undefined ? {} : { portionId: portion.id }),
        },
        buildPreview(
          portion === undefined
            ? null
            : composePortionLabel(portion.label, portion.quantity, baseUnit),
        ),
      );
      return;
    }

    // Voie OFF : importer, puis logger la fiche qui vient d'être créée.
    importFood.mutate(undefined, {
      onSuccess: (created) => {
        const portion =
          mode.portionIndex === null ? undefined : created.portions[mode.portionIndex];
        onValidate(
          {
            type: 'food',
            foodId: created.id,
            quantity: mode.portionIndex === null ? baseQuantity : typed,
            ...(portion === undefined ? {} : { portionId: portion.id }),
          },
          buildPreview(
            portion === undefined
              ? null
              : composePortionLabel(portion.label, portion.quantity, created.baseUnit),
          ),
        );
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
        {onBack && (
          <Button variant="secondary" shape="rounded" onClick={onBack}>
            ‹ Retour
          </Button>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{name}</p>
          <p className="n-hint">
            {brand === null || brand === '' ? '' : `${brand} · `}
            {isOff ? 'Open Food Facts' : 'Ma base'} → {mealName}
          </p>
        </div>
      </div>

      {/*
        Unité de base et portions, côte à côte : changer de mode est un tap
        (specs §6.3, « sans quitter l'écran »).

        ⚠️ Masqué quand l'aliment n'a **aucune portion** : un choix à une seule
        option n'est pas un choix, et le champ juste en dessous annonce déjà son
        unité (« Quantité (g) »). Constaté en relisant le rendu — un chiffre de
        segments égal à un laissait une pastille « g » orpheline sur l'écran le
        plus emprunté de l'app.
      */}
      {modes.length > 1 && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        <span className="n-field__label">Quantité exprimée en</span>
        <div className="n-chip-row">
          {modes.map((item, index) => (
            /*
              ⚠️ Le `Button` du système porte déjà `min-width: 44px` : le segment
              de l'unité de base ne fait qu'une ou deux lettres (« g », « ml »)
              et tombait sous la cible tactile des specs §8.1 quand il portait
              sa largeur lui-même.
            */
            <Button
              key={`${String(item.portionIndex)}-${item.label}`}
              aria-pressed={index === modeIndex}
              variant={index === modeIndex ? 'selected' : 'secondary'}
              onClick={() => {
                setModeIndex(index);
                setQuantity(
                  item.portionIndex === null ? DEFAULT_BASE_QUANTITY : DEFAULT_PORTION_QUANTITY,
                );
              }}
            >
              {item.portionIndex === null
                ? item.label
                : `${item.label} · ${String(item.baseValue)} ${baseUnit}`}
            </Button>
          ))}
        </div>
      </div>
      )}

      <NumberField
        label={mode.portionIndex === null ? `Quantité (${baseUnit})` : `Nombre de « ${mode.label} »`}
        value={quantity}
        onChange={setQuantity}
      />

      {mode.portionIndex !== null && (
        <Hint>
          Soit {formatMacro(baseQuantity, baseUnit)} {baseUnit}.
        </Hint>
      )}

      {/* Macros OFF : visibles ET éditables avant validation (specs §6.4). */}
      {isOff && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <span className="n-field__label">
            Valeurs Open Food Facts, pour 100 {baseUnit} — à vérifier
          </span>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: '0.5rem',
            }}
          >
            {MACRO_ROWS.map((row) => (
              <NumberField
                key={row.key}
                label={row.short}
                suffix={row.key === 'kcal' ? undefined : 'g'}
                value={offFields[row.key]}
                onChange={(value) => {
                  setOffFields((current) => ({ ...current, [row.key]: value }));
                }}
              />
            ))}
          </div>
          <Hint>
            Open Food Facts est alimenté par ses contributeurs : ces valeurs peuvent être fausses.
            Les corriger ici les corrige aussi dans la fiche qui sera créée.
          </Hint>
        </div>
      )}

      {/* Aperçu du total — recalculé par le serveur à la validation (§5.1). */}
      <Card>
        <div className="n-preview">
          {MACRO_ROWS.map((row) => (
            <div key={row.key}>
              <div className="n-preview__label">
                <MacroDot macro={row.key} />
                {row.short}
              </div>
              <div className="n-preview__value tnum">
                {formatMacro(preview[row.key], row.unit)}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {importFood.isError && <ErrorNote>{importFood.error.message}</ErrorNote>}

      <Button
        variant="primary"
        shape="rounded"
        block
        onClick={submit}
        disabled={!canSubmit || importFood.isPending}
      >
        {importFood.isPending ? 'Import…' : `Ajouter à ${mealName}`}
      </Button>

      {isOff && (
        <Hint>
          Ce produit sera enregistré dans votre base personnelle : la prochaine fois, il sera
          trouvé sans passer par Open Food Facts.
        </Hint>
      )}
    </div>
  );
}
